import {
  Box,
  Typography,
  TextField,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { getServices } from "../../services/api/getAll";
import {
  addService,
  deleteService,
  editService,
} from "../../services/api/adminService";

const languages = ["en", "ar", "es"];

const ServicesAdminScreen = () => {
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<any>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [newServiceData, setNewServiceData] = useState<any>({
    translations: {},
    imageFile: null,
  });
  const [def, setDef]: any = useState();

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await getServices();
        const items = response?.items ?? [];
        const formatted = items.map((item: any) => {
          const translations: any = {};
          item.translations?.forEach((t: any) => {
            translations[t.languageCode] = {
              title: t.title,
              description: t.description,
            };
          });
          return {
            id: item.id,
            imageUrl: item.imageUrl,
            translations,
          };
        });
        setServices(formatted);
      } catch (e) {
        console.error("Error loading services", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewServiceData({ translations: {}, imageFile: null });
  };

  const handleDialogChange = (lang: string, field: string, value: string) => {
    setNewServiceData((prev: any) => {
      const updated = { ...prev };
      if (!updated.translations[lang]) updated.translations[lang] = {};
      updated.translations[lang][field] = value;
      return updated;
    });
  };

  const handleAddService = async () => {
    const formData = new FormData();
    let transIndex = 0;

    for (const lang of languages) {
      const title = newServiceData.translations?.[lang]?.title ?? "";
      const description =
        newServiceData.translations?.[lang]?.description ?? "";
      if (title || description) {
        formData.append(`translations[${transIndex}].languageCode`, lang);
        formData.append(`translations[${transIndex}].title`, title);
        formData.append(`translations[${transIndex}].description`, description);
        transIndex++;
      }
    }

    if (newServiceData.imageFile) {
      formData.append("image", newServiceData.imageFile);
    }

    try {
      await addService(formData);
      handleCloseDialog();
      setLoading(true);
      const response = await getServices();
      const items = response?.items ?? [];
      const formatted = items.map((item: any) => {
        const translations: any = {};
        item.translations?.forEach((t: any) => {
          translations[t.languageCode] = {
            title: t.title,
            description: t.description,
          };
        });
        return {
          id: item.id,
          imageUrl: item.imageUrl,
          translations,
        };
      });
      setServices(formatted);
    } catch (e) {
      console.error("Error adding service", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteService(id);
      setServices((s) => s.filter((x) => x.id !== id));
    } catch (e) {
      console.error("Error deleting", e);
    }
  };

  const openEditDialog = (index: number) => {
    const deepCopy = JSON.parse(JSON.stringify(services[index]));
    setEditIndex(index);
    setDef(deepCopy);
    setEditedData(deepCopy);
    setImageFile(null);
    setEditDialogOpen(true);
  };

  const handleEditChange = (lang: string, field: string, value: string) => {
    setEditedData((prev: any) => {
      const updated = { ...prev };
      if (!updated.translations[lang]) updated.translations[lang] = {};
      updated.translations[lang][field] = value;
      return updated;
    });
  };

  const handleSaveEdit = async () => {
    if (editIndex === null) return;
    const formData = new FormData();
    let transIndex = 0;

    for (const lang of languages) {
      const title = editedData.translations?.[lang]?.title ?? "";
      const description = editedData.translations?.[lang]?.description ?? "";
      if (title || description) {
        formData.append(`translations[${transIndex}].languageCode`, lang);
        formData.append(`translations[${transIndex}].title`, title);
        formData.append(`translations[${transIndex}].description`, description);
        transIndex++;
      }
    }

    formData.append("id", editedData.id.toString());

    if (imageFile) {
      formData.append("image", imageFile);
    }

    try {
      await editService(formData);
      setEditDialogOpen(false);
      const response = await getServices();
      const items = response?.items ?? [];
      const formatted = items.map((item: any) => {
        const translations: any = {};
        item.translations?.forEach((t: any) => {
          translations[t.languageCode] = {
            title: t.title,
            description: t.description,
          };
        });
        return {
          id: item.id,
          imageUrl: item.imageUrl,
          translations,
        };
      });
      setServices(formatted);
    } catch (e) {
      console.error("Error saving edit", e);
    }
  };

  if (loading) {
    return (
      <Box
        height="80vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 5 }, bgcolor: "#f4f7fa" }}>
        <Box display="flex" justifyContent="space-between" mb={4}>
          <Typography variant="h4" fontWeight="bold">
            Services
          </Typography>
          <Button variant="contained" onClick={handleOpenDialog}>
            + Add New Service
          </Button>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={3} justifyContent="flex-start">
          {services.map((service, index) => (
            <Paper
              key={service.id}
              elevation={6}
              sx={{
                width: {
                  xs: "90%",
                  sm: "calc(45% - 12px)",
                  md: "calc(30.333% - 16px)",
                },
                minHeight: 320,
                p: 2,
                borderRadius: 4,
                bgcolor: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb={2}
              >
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: "#f5f5f5",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    mb: 2,
                  }}
                >
                  <img
                    src={service.imageUrl}
                    alt="service"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                {languages.map((lang) => (
                  <Box
                    key={lang}
                    width="100%"
                    mt={1}
                    sx={{
                      borderTop: "1px solid #e2e8f0",
                      pt: 1,
                      px: 1,
                    }}
                  >
                    <Typography
                      fontWeight="600"
                      fontSize="13px"
                      color="#0ea5e9"
                      textTransform="uppercase"
                      textAlign="center"
                      mb={0.5}
                    >
                      {lang}
                    </Typography>
                    <Typography
                      fontWeight="bold"
                      fontSize="16px"
                      color="#0f172a"
                      textAlign="center"
                    >
                      {service.translations?.[lang]?.title ?? "No Title"}
                    </Typography>
                    <Typography
                      fontSize="13px"
                      color="#334155"
                      textAlign="center"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        mt: 0.5,
                      }}
                    >
                      {service.translations?.[lang]?.description ??
                        "No Description"}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box display="flex" justifyContent="center" gap={2}>
                <IconButton
                  onClick={() => openEditDialog(index)}
                  sx={{
                    bgcolor: "green",
                    "&:hover": { bgcolor: "primary.main", color: "#fff" },
                    borderRadius: 2,
                  }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  onClick={() => handleDelete(service.id)}
                  sx={{
                    bgcolor: "error.light",
                    "&:hover": { bgcolor: "error.main", color: "#fff" },
                    borderRadius: 2,
                  }}
                >
                  <Delete />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Add New Service</DialogTitle>
          <DialogContent dividers>
            {languages.map((lang) => (
              <Box key={lang} mb={3}>
                <TextField
                  label={`Title (${lang})`}
                  fullWidth
                  value={newServiceData.translations?.[lang]?.title ?? ""}
                  onChange={(e) =>
                    handleDialogChange(lang, "title", e.target.value)
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  label={`Description (${lang})`}
                  fullWidth
                  multiline
                  minRows={3}
                  value={newServiceData.translations?.[lang]?.description ?? ""}
                  onChange={(e) =>
                    handleDialogChange(lang, "description", e.target.value)
                  }
                />
              </Box>
            ))}
            <Button variant="outlined" component="label">
              Upload Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setNewServiceData((prev: any) => ({
                      ...prev,
                      imageFile: e.target.files![0],
                    }));
                  }
                }}
              />
            </Button>
            {newServiceData.imageFile && (
              <Typography mt={1}>
                Selected: {newServiceData.imageFile.name}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleAddService} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            if (editIndex !== null) {
              setEditedData(def);
            }
            setImageFile(null);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Edit Service</DialogTitle>
          <DialogContent dividers>
            {languages.map((lang) => (
              <Box key={lang} mb={3}>
                <TextField
                  label={`Title (${lang})`}
                  fullWidth
                  value={editedData.translations?.[lang]?.title ?? ""}
                  onChange={(e) =>
                    handleEditChange(lang, "title", e.target.value)
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  label={`Description (${lang})`}
                  fullWidth
                  multiline
                  minRows={3}
                  value={editedData.translations?.[lang]?.description ?? ""}
                  onChange={(e) =>
                    handleEditChange(lang, "description", e.target.value)
                  }
                />
              </Box>
            ))}
            {imageFile ? (
              <Box mt={2}>
                <img
                  src={URL.createObjectURL(imageFile)}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box mt={2}>
                <img
                  src={editedData.imageUrl}
                  alt="Current"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}

            <Button variant="outlined" component="label">
              Upload Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setImageFile(e.target.files![0]);
                  }
                }}
              />
            </Button>
            {imageFile && (
              <Typography mt={1}>Selected: {imageFile.name}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEditDialogOpen(false);
                if (editIndex !== null) {
                  setEditedData({ ...services[editIndex] });
                }
                setImageFile(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default ServicesAdminScreen;
